import { useMemo } from 'react';
import { DataTable, type Column } from '@/components/ui/DataTable';
import {
  formatTableCellValue,
  humanizeColumnKey,
  inferRecordColumnKeys,
  normalizeRecordRows,
  recordRowKey,
} from '@/utils/tableCellValue';

interface RecordDataTableProps {
  data: unknown[] | null | undefined;
  columns?: string[];
  excludeKeys?: string[];
  keyField?: string;
  loading?: boolean;
  embedded?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Rows per page for automatic client-side pagination. */
  pageSize?: number;
}

/** Renders API record arrays as readable tables (no raw JSON). */
export function RecordDataTable({
  data,
  columns,
  excludeKeys,
  keyField = 'id',
  loading,
  embedded,
  emptyTitle = 'No records found',
  emptyDescription,
  pageSize,
}: RecordDataTableProps) {
  const rows = useMemo(() => normalizeRecordRows(data), [data]);

  const columnKeys = useMemo(
    () => columns ?? inferRecordColumnKeys(rows, excludeKeys),
    [columns, rows, excludeKeys],
  );

  const tableColumns = useMemo<Column<Record<string, unknown>>[]>(
    () =>
      columnKeys.map((key) => ({
        key,
        header: humanizeColumnKey(key),
        render: (row) => formatTableCellValue(row[key]),
      })),
    [columnKeys],
  );

  return (
    <DataTable
      columns={tableColumns}
      data={rows}
      keyField={(row) => recordRowKey(row, rows.indexOf(row), keyField)}
      loading={loading}
      embedded={embedded}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      pageSize={pageSize}
    />
  );
}
